import { WHOLE_QUANTITY_MESSAGE } from '@nudool/shared';
import { useId, type ComponentProps } from 'react';

import { DimeFieldError } from './DimeField.js';
import { DimeInput } from './DimeInput.js';
import { useDimeQuantityValidation } from './hooks/use-dime-quantity-validation.js';

type DimeQuantityInputProps = Readonly<
  Omit<ComponentProps<typeof DimeInput>, 'type' | 'inputMode' | 'step'> & {
    integral: boolean;
    /** The form's error for this field, shown under the input. Skipped when it repeats the whole-number message the input already shows. */
    error?: string;
  }
>;

export function DimeQuantityInput({
  integral,
  value,
  error,
  'aria-describedby': describedBy,
  'aria-invalid': ariaInvalid,
  ...props
}: DimeQuantityInputProps) {
  const { isViolating, errorId } = useDimeQuantityValidation(value, integral);
  const fieldErrorId = useId();
  const fieldError = isViolating && error === WHOLE_QUANTITY_MESSAGE ? undefined : error;
  const describedByIds = [describedBy, isViolating && errorId, fieldError && fieldErrorId]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <DimeInput
        {...props}
        value={value}
        type="number"
        inputMode={integral ? 'numeric' : 'decimal'}
        step={integral ? 1 : 'any'}
        aria-invalid={ariaInvalid || isViolating || Boolean(fieldError) || undefined}
        aria-describedby={describedByIds || undefined}
      />
      {isViolating ? (
        <DimeFieldError id={errorId} className="max-w-full">
          {WHOLE_QUANTITY_MESSAGE}
        </DimeFieldError>
      ) : null}
      {fieldError ? (
        <DimeFieldError id={fieldErrorId} className="max-w-full">
          {fieldError}
        </DimeFieldError>
      ) : null}
    </>
  );
}
